import axios from 'axios';

export const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
export const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const PHOTO_ACCEPT = '.jpg,.jpeg,.png,.webp';
export const HEIC_MESSAGE = "HEIC/HEIF photos aren't supported yet. Export as JPEG or choose a JPEG, PNG, or WebP photo.";

const HEIC_NAME = /\.(heic|heif)$/i;

// Returns a message explaining why a file can't be uploaded, or null.
export const validatePhotoFile = (file) => {
  const type = (file.type || '').toLowerCase();
  if (type.startsWith('image/hei') || HEIC_NAME.test(file.name || '')) return HEIC_MESSAGE;
  if (!ALLOWED_PHOTO_TYPES.includes(type)) return `${file.name} is not a JPEG, PNG, or WebP photo.`;
  if (!file.size) return `${file.name} is empty.`;
  if (file.size > MAX_PHOTO_BYTES) return `${file.name} is larger than 10 MB.`;
  return null;
};

// Creating a request returns the request itself, or { data, message } when
// the confirmation emails failed.
export const getCreatedRequestId = (responseData) => responseData?.id ?? responseData?.data?.id;

const serverError = (error, fallback) => error?.response?.data?.error || fallback;

// `resume` is set when the file already reached storage, so a retry only
// repeats verification instead of uploading a duplicate photo.
export class PhotoUploadError extends Error {
  constructor(message, resume = null) {
    super(message);
    this.resume = resume;
  }
}

// Verification can be repeated safely unless the server rejected the photo.
const canResumeVerification = (error) => error?.response?.status !== 400 && error?.response?.status !== 404;

// Initiate, send to S3, then verify. Resolves only once the photo is
// verified and will appear in the request's gallery. Pass a previous
// failure's `resume` to retry just the verification.
export const uploadRequestPhoto = async (api, requestId, file, resume = null) => {
  const base = `/request-manager/requests/${requestId}/photos`;
  let photoId = resume?.photoId;
  if (!photoId) {
    let upload;
    try {
      const response = await api.post(`${base}/initiate/`, {
        filename: file.name,
        content_type: file.type,
        size: file.size,
      });
      ({ upload, photo_id: photoId } = response.data);
    } catch (error) {
      throw new PhotoUploadError(serverError(error, 'The upload could not be started.'));
    }

    const body = new FormData();
    Object.entries(upload.fields).forEach(([key, value]) => body.append(key, value));
    body.append('file', file); // S3 requires the file to be the last field.
    try {
      // Plain axios: S3 must not receive the app's Authorization header.
      await axios.post(upload.url, body);
    } catch (error) {
      throw new PhotoUploadError('The photo could not be sent to storage.');
    }
  }

  try {
    await api.post(`${base}/${photoId}/complete/`);
  } catch (error) {
    throw new PhotoUploadError(
      serverError(error, 'The photo could not be verified.'),
      canResumeVerification(error) ? { photoId } : null,
    );
  }
};

// Accepts files, or { file, resume } entries from a previous attempt.
// Uploads every one and reports each result; never rejects.
export const uploadRequestPhotos = (api, requestId, entries) => Promise.all(entries.map(async (entry) => {
  const { file, resume = null } = entry instanceof Blob ? { file: entry } : entry;
  const problem = validatePhotoFile(file);
  if (problem) return { file, ok: false, error: problem, resume: null };
  try {
    await uploadRequestPhoto(api, requestId, file, resume);
    return { file, ok: true };
  } catch (error) {
    return { file, ok: false, error: error.message, resume: error.resume || null };
  }
}));
